import React, { useState } from 'react';
import { AccordionProps, AccordionVariant } from '../../../types/DesignSystem/Accordion';
import { ChevronDown } from '../Iconography/Icons';
import { FOCUS_RING_INSET } from '../focusRing';
import { withClassName } from '../cn';

/** § 341 — the wrapper's frame per variant; `boxed` is the recipe 15 call sites hand-rolled. */
const variantFrame: Record<AccordionVariant, string> = {
  divided: 'tw-border-t tw-border-edge',
  boxed: 'tw-border tw-border-edge tw-rounded-ctl tw-bg-surface tw-px-4',
};

/** See the type file (§ 68) for the contract and the trio this supersedes. */
/*
 * § 405 — the header button's heading, when the caller names a level (the APG accordion
 * structure). It holds the button and nothing else; `m-0` drops the margin a heading carries
 * (preflight and the legacy stylesheets disagree on it), and the button states its own type, so
 * the row renders as it did without one. No level, no element: the markup is unchanged.
 */
const HeadingFrame = ({ level, testId, children }: {
  level?: 2 | 3 | 4 | 5 | 6;
  testId?: string;
  children: React.ReactNode;
}) => (level === undefined
  ? <>{children}</>
  : React.createElement(`h${level}`, { className: 'tw-m-0 tw-flex tw-flex-1 tw-min-w-0', 'data-testid': testId }, children));

const Accordion = React.forwardRef<HTMLButtonElement, AccordionProps>(function Accordion({
  title,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  actions,
  variant = 'divided',
  headingLevel,
  children,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const uid = React.useId();
  const headerId = `${uid}-header`;
  const regionId = `${uid}-region`;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  /* Controlled mode (§ 90): with `open` provided, the caller owns the state and
     a click only REPORTS intent through onOpenChange. */
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const toggle = () => {
    const next = !open;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div className={withClassName(variantFrame[variant], className)}>
      {/* One header row: the disclosure button fills it, `actions` (§ 192) sit
          beside it as SIBLINGS — a button inside a button is invalid HTML, and
          an action click must not toggle the section. */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <HeadingFrame level={headingLevel} testId={dataTestId ? `${dataTestId}-heading` : undefined}>
          <button
            /* rest first — see the rule in Button.tsx. */
            {...rest}
            ref={ref}
            type="button"
            id={headerId}
            aria-expanded={open}
            aria-controls={regionId}
            onClick={toggle}
            data-testid={dataTestId}
            /* `select-none` (§ 235): a double-click toggles the section twice AND
               flashed a selection across the title — the title is truncated anyway,
               so selection never offered a full copy. */
            className={`tw-flex tw-flex-1 tw-min-w-0 tw-items-center tw-justify-between tw-gap-2 tw-py-3 tw-text-left tw-text-title-header tw-text-ink tw-select-none hover:tw-text-accent motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING_INSET}`}
          >
            <span className="tw-flex-1 tw-truncate">{title}</span>
            <ChevronDown
              aria-hidden="true"
              className={`tw-shrink-0 tw-text-ink-tertiary motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift ${open ? 'tw-rotate-180' : ''}`}
            />
          </button>
        </HeadingFrame>
        {actions && (
          <div
            className="tw-flex tw-items-center tw-gap-2 tw-shrink-0"
            data-testid={dataTestId ? `${dataTestId}-actions` : undefined}
          >
            {actions}
          </div>
        )}
      </div>
      {/* Unmounted when closed — the § 68 reset contract; see the type file.
          § 223: mounting runs `section-reveal` — 160ms fade + 4px rise on the
          settle curve, so opening UNFOLDS out of the header instead of appearing
          (§ 219 used the generic overlay fade here; a region is not an overlay).
          Closing stays instant, exactly because of that unmount contract
          (holding content on screen to animate it would defeat the reset). */}
      {open && (
        <div
          role="region"
          id={regionId}
          aria-labelledby={headerId}
          className="motion-safe:tw-animate-section-reveal"
          data-testid={dataTestId ? `${dataTestId}-region` : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
});

export default Accordion;
